/**
 * One event row: its checkbox, badges and organization count indicator.
 */

import React from 'react';
import { Checkbox, Label, Table } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { formatDateByCountry } from '../../../../helpers/dateAndTimeHelper';
import { i18nKeys } from '../constants';
import { formatLocation, isClosed, isExclusive, isLowCount } from '../helpers';
import { OrganizationEvent } from '../types';
import styles from '../AddOrganizationToEvents.module.css';

interface EventTableRowProps {
  event: OrganizationEvent;
  isPicked: boolean;
  disabled: boolean;
  onToggle: (event: OrganizationEvent, isSelected: boolean) => void;
}

const OrganizationCount: React.FC<{ event: OrganizationEvent }> = ({
  event
}) => {
  if (isExclusive(event)) {
    return (
      <Label color="red" size="small">
        {event.organizationsCount}{' '}
        <Translate value={i18nKeys.EVENTS.EXCLUSIVE} />
      </Label>
    );
  }
  if (isLowCount(event)) {
    return (
      <Label
        color="orange"
        size="small"
        title={I18n.t(i18nKeys.EVENTS.LOW_COUNT)}
      >
        {event.organizationsCount} <Translate value={i18nKeys.EVENTS.LOW} />
      </Label>
    );
  }
  return <>{event.organizationsCount}</>;
};

const EventTableRow: React.FC<EventTableRowProps> = ({
  event,
  isPicked,
  disabled,
  onToggle
}) => {
  const isPickedExclusive = isPicked && isExclusive(event);

  return (
    <Table.Row disabled={event.alreadyAdded} warning={isPickedExclusive}>
      <Table.Cell collapsing>
        {/* Semantic forwards title to the input, which gives each checkbox its accessible name. */}
        <Checkbox
          title={I18n.t(i18nKeys.EVENTS.SELECT_EVENT, { name: event.name })}
          checked={event.alreadyAdded || isPicked}
          disabled={event.alreadyAdded || disabled}
          onChange={(_, { checked }) => onToggle(event, !!checked)}
        />
      </Table.Cell>
      <Table.Cell>
        {event.name}
        {event.alreadyAdded && (
          <Label basic size="tiny" className={styles.rowBadge}>
            <Translate value={i18nKeys.EVENTS.ALREADY_ADDED} />
          </Label>
        )}
        {isPickedExclusive && (
          <Label color="orange" size="tiny" className={styles.rowBadge}>
            <Translate value={i18nKeys.EVENTS.SINGLE_ORGANIZATION_PICKED} />
          </Label>
        )}
      </Table.Cell>
      <Table.Cell>{formatLocation(event)}</Table.Cell>
      <Table.Cell collapsing textAlign="center">
        <OrganizationCount event={event} />
      </Table.Cell>
      <Table.Cell collapsing>
        {formatDateByCountry(event.start ?? '', event.country || '')}
        {isClosed(event) && (
          <Label color="red" basic size="tiny" className={styles.rowBadge}>
            <Translate value={i18nKeys.EVENTS.CLOSED} />
          </Label>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

export default EventTableRow;
